import { Link } from "react-router-dom";
import "./CreateEventRegistrationInvite.css";
import Button from "../../../components/Button";
import { useAppState } from "../../../utils/appState";
import ModalForm from "../../ModalForm";
import { useQuery } from "@apollo/client";
import {
  EVENT_SUGGESTED_PARTICIPANTS,
  CREATE_EVENT_REGISTRATION_INVITE,
} from "../../../api/event";
import captureException from "../../../utils/captureException";
import { useState, useEffect } from "react";
import Spinner from "../../../components/Spinner";
import { ReactComponent as TimesIcon } from "../../../icons/times.svg";
import { ReactComponent as ShareIcon } from "../../../icons/share.svg";
import QRCode from "react-qr-code";

function CreateEventRegistrationInviteConfirmation({ invite, reset }) {
  const [, setAppState] = useAppState();
  const [copied, setCopied] = useState(false);
  const dismiss = () => {
    setAppState({ modal: false });
  };
  const url = `https://beta.sportspaddock.com/accept-registration-invite/${invite?.id}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
  };

  const shareUrl = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Event Registration Invite",
          text: "Join " + invite?.event?.name + " on Sports Paddock",
          url: url,
        });
      } else {
        // Fallback to copying if Web Share API is not supported
        copyToClipboard();
      }
    } catch (error) {
      // Handle any errors (including if user cancels the share)
      console.log("Error sharing:", error);
    }
  };

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  return (
    <div className="modal-form-mobile-wrapper">
      <div className="modal-form-mobile">
        <div className="modal-form-mobile__title">
          <div className="modal-form-mobile__title__text">
            Invite Created Successfully
          </div>
          <div
            className="modal-form-mobile__title__dismiss-btn"
            onClick={dismiss}
          >
            <TimesIcon style={{ stroke: "var(--dark-3)", height: "12px" }} />
          </div>
        </div>

        <div className="modal-form-mobile__body">
          <div className="create-event-registration-invite__qr-container">
            <QRCode value={url} size={256} level="H" />
          </div>
          <div className="create-event-registration-invite__link-container">
            <div className="create-event-registration-invite__link-container__value">
              {url}
            </div>
            <div
              className="create-event-registration-invite__link-container__copy-btn"
              onClick={copyToClipboard}
            >
              {copied ? "Copied!" : "Copy Link"}
            </div>
          </div>
        </div>

        <div className="create-event-registration-invite__options-container">
          <div
            className="create-event-registration-invite__options-container__option"
            onClick={shareUrl}
          >
            <ShareIcon
              style={{
                stroke: "var(--dark-3)",
                height: "12px",
                marginRight: "8px",
              }}
            />
            Click here to share
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateEventRegistrationInviteForm({ id }) {
  const [success, setSuccess] = useState(false);
  const { loading, error, data } = useQuery(EVENT_SUGGESTED_PARTICIPANTS, {
    variables: { id },
  });

  if (error) {
    captureException({
      error,
      info: {
        type: "query",
        component: "CreateEventRegistrationInvite",
        query: "EVENT_SUGGESTED_PARTICIPANTS",
      },
    });
    console.log(error);
    return <div>Error</div>;
  }

  if (loading) {
    return (
      <div>
        <Spinner />
      </div>
    );
  }

  const ev = data?.event;
  const participants = ev?.suggestedParticipants || [];

  if (success) {
    return (
      <CreateEventRegistrationInviteConfirmation
        invite={success}
        reset={() => {
          setSuccess(false);
        }}
      />
    );
  }

  return (
    <ModalForm
      title={"Invite Player"}
      style={{ body: { overflow: "auto" } }}
      config={{
        doNotHideOnCompleted: true,
        fields: [
          {
            label: "Name",
            type: "text",
            key: "name",
            formValue: "",
          },
          {
            label: "Email",
            type: "email",
            key: "email",
            formValue: "",
          },
          {
            label: "Team",
            type: "select",
            key: "organization",
            formValue: "",
            items: participants.map((p) => ({
              label: p.name,
              value: p.id,
            })),
          },
        ],
        mutation: CREATE_EVENT_REGISTRATION_INVITE,
        onSubmit: ({ fields, mutate }) => {
          // console.log("Submitted:", fields);
          const name = fields[0].formValue;
          const email = fields[1].formValue;
          const organization = fields[2].formValue;

          if (!name || !email) {
            return;
          }

          const vars = {
            event: { connect: { id } },
            name,
            email,
            organization: organization
              ? { connect: { id: organization } }
              : undefined,
          };

          mutate({
            variables: { data: vars },
            update: (cache, result) => {
              setSuccess(result.data.createEventRegistrationInvite);
            },
          });
        },
      }}
    />
  );
}

export default function CreateEventRegistrationInvite({ id }) {
  const [, setAppState] = useAppState();

  return (
    <div className="manage-event-prompt-mobile">
      <div className="manage-event-prompt-mobile__text">
        <div className="manage-event-prompt-mobile__text__primary">
          Invite Players
        </div>

        <div className="manage-event-prompt-mobile__text__secondary">
          Generate custom registration links for your players to make it easier
          for them to register.
        </div>
      </div>

      <Button
        onClick={() => {
          setAppState({
            modal: <CreateEventRegistrationInviteForm id={id} />,
          });
        }}
        className="primary blue medium"
        style={{ marginTop: "12px" }}
      >
        Invite Player
      </Button>
    </div>
  );
}
